import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BadgeList from '../../components/BadgeList';
import { determineUserBadges } from '../../utils/badgeUtils';
import 'react-tooltip/dist/react-tooltip.css';

const MemberDirectory = () => {
  // Mock data for members
  const members = [
    // Add badge properties to mock data
    ...[
    {
      id: 1,
      name: 'John Smith',
      title: 'Investment Banking Associate',
      company: 'Goldman Sachs',
      location: 'New York, NY',
      specialty: 'Investment Banking',
      tier: 'Tier 2',
      image: '/images/members/member1.jpg'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      title: 'Private Equity Analyst',
      company: 'Blackstone',
      location: 'New York, NY',
      specialty: 'Private Equity',
      tier: 'Tier 1',
      image: '/images/members/member2.jpg'
    },
    {
      id: 3,
      name: 'Michael Chen',
      title: 'VP of Corporate Development',
      company: 'Morgan Stanley',
      location: 'Chicago, IL',
      specialty: 'Corporate Development',
      tier: 'Tier 3',
      image: '/images/members/member3.jpg'
    },
    {
      id: 4,
      name: 'Emily Rodriguez',
      title: 'Tax Manager',
      company: 'Deloitte',
      location: 'Los Angeles, CA',
      specialty: 'Accounting',
      tier: 'Tier 2',
      image: '/images/members/member4.jpg'
    },
    {
      id: 5,
      name: 'David Kim',
      title: 'Associate Attorney',
      company: 'Skadden',
      location: 'Boston, MA',
      specialty: 'Law Firm',
      tier: 'Tier 2',
      image: '/images/members/member5.jpg'
    },
    {
      id: 6,
      name: 'Jessica Lee',
      title: 'Investment Banking Analyst',
      company: 'JP Morgan',
      location: 'San Francisco, CA',
      specialty: 'Investment Banking',
      tier: 'Tier 1',
      image: '/images/members/member6.jpg'
    },
    {
      id: 7,
      name: 'Robert Wilson',
      title: 'Director',
      company: 'KPMG',
      location: 'Dallas, TX',
      specialty: 'Valuation',
      tier: 'Tier 3',
      image: '/images/members/member7.jpg'
    },
    {
      id: 8,
      name: 'Amanda Thompson',
      title: 'Wealth Management Associate',
      company: 'UBS',
      location: 'Miami, FL',
      specialty: 'Wealth Manager',
      tier: 'Tier 2',
      image: '/images/members/member8.jpg'
    }
  ].map(member => ({
    ...member,
    // Add badge properties for demonstration
    isFoundingMember: member.id === 1 || member.id === 5,
    isVipMember: member.id === 2 || member.id === 6,
    memberSince: member.id === 3 ? 'May 2018' : member.id === 7 ? 'January 2015' : member.id === 1 ? 'March 2019' : 'June 2023',
    role: member.id === 4 ? 'Chapter President' : '',
    chapter: member.location.split(',')[0],
    referralCount: member.id === 8 ? 7 : member.id === 3 ? 12 : 0,
    ambassadorSince: member.id === 8 ? 'January 2024' : member.id === 3 ? 'June 2020' : null,
    // Calculate badges for each member
    badges: []
  }))];

  // Calculate badges for each member
  members.forEach(member => {
    member.badges = determineUserBadges(member);
  });

  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [networkConnections, setNetworkConnections] = useState([2, 5]); // Mock network connections
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(8); // Number of members to display per page

  // Get unique values for filter dropdowns
  const locations = [...new Set(members.map(member => member.location))];

  // Define specialties based on company types from Groups/Forums section
  const specialties = [
    'Wealth Manager',
    'Management Consultant',
    'Valuation',
    'Investment Banking',
    'Private Equity',
    'Venture Capital',
    'Law Firm',
    'Accounting',
    'Financial Planning',
    'Corporate Development',
    'Healthcare Finance'
  ];

  const connections = ['1st', '2nd', '3rd+'];
  const tiers = [...new Set(members.map(member => member.tier))];

  // Add connection levels to members (mock data for demonstration)
  const membersWithConnections = members.map(member => ({
    ...member,
    connectionLevel: member.id <= 2 ? '1st' : member.id <= 5 ? '2nd' : '3rd+'
  }));

  // Filter members based on search and filters
  const filteredMembers = membersWithConnections.filter(member => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.company.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === '' || member.location === locationFilter;
    const matchesSpecialty = specialtyFilter === '' || member.specialty === specialtyFilter;
    const matchesConnection = connectionFilter === '' || member.connectionLevel === connectionFilter;
    const matchesTier = tierFilter === '' || member.tier === tierFilter;

    return matchesSearch && matchesLocation && matchesSpecialty && matchesConnection && matchesTier;
  });

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Member Directory</h1>
          </div>
        </div>
      </div>

      {/* Directory Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  placeholder="Search members by name, title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${
                    viewMode === 'list'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Location
                </label>
                <select
                  id="location-filter"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="specialty-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Specialty
                </label>
                <select
                  id="specialty-filter"
                  value={specialtyFilter}
                  onChange={(e) => setSpecialtyFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Specialties</option>
                  {specialties.map((specialty, index) => (
                    <option key={index} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="connection-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Connection
                </label>
                <select
                  id="connection-filter"
                  value={connectionFilter}
                  onChange={(e) => setConnectionFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Connections</option>
                  {connections.map((connection, index) => (
                    <option key={index} value={connection}>{connection}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="tier-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Tier
                </label>
                <select
                  id="tier-filter"
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Tiers</option>
                  {tiers.map((tier, index) => (
                    <option key={index} value={tier}>{tier}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-gray-600">
              Showing {Math.min(currentPage * membersPerPage, filteredMembers.length)} of {filteredMembers.length} members (Page {currentPage})
            </p>
          </div>

          {/* Member List */}
          {filteredMembers.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredMembers
                  .slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage)
                  .map(member => (
                  <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    <div className="p-6 text-center">
                      <div className="w-24 h-24 rounded-full bg-gray-300 mx-auto mb-4">
                        {/* Member image placeholder */}
                        <div className="h-full w-full flex items-center justify-center">
                          <span className="text-gray-600 text-xs">Photo</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <h3 className="text-lg font-bold">{member.name}</h3>
                        {member.badges && member.badges.length > 0 && (
                          <BadgeList badges={member.badges} size="small" />
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{member.title}</p>
                      <p className="text-gray-600 text-sm mb-4">{member.company}</p>
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        <p>{member.location}</p>
                        <p>Specialty: {member.specialty}</p>
                        <p>{member.tier}</p>
                      </div>
                      <div className="space-y-2">
                        {networkConnections.includes(member.id) ? (
                          <div className="flex items-center justify-center text-sm text-green-600 mb-2">
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            In Your Network
                          </div>
                        ) : member.id !== 1 && (
                          <button
                            onClick={() => setNetworkConnections([...networkConnections, member.id])}
                            className="block w-full bg-green-600 text-white text-center py-1 px-4 rounded text-sm hover:bg-green-700 transition duration-200"
                          >
                            Add to Network
                          </button>
                        )}
                        <Link
                          to={`/directory/${member.id}`}
                          className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                        >
                          View Profile
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMembers
                  .slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage)
                  .map(member => (
                  <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    <div className="p-6 flex items-center">
                      <div className="w-16 h-16 rounded-full bg-gray-300 mr-6 flex-shrink-0">
                        {/* Member image placeholder */}
                        <div className="h-full w-full flex items-center justify-center">
                          <span className="text-gray-600 text-xs">Photo</span>
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-1">
                          <h3 className="text-lg font-bold">{member.name}</h3>
                          {member.badges && member.badges.length > 0 && (
                            <BadgeList badges={member.badges} size="small" />
                          )}
                        </div>
                        <p className="text-gray-600">{member.title} at {member.company}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            {member.location}
                          </span>
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            {member.specialty}
                          </span>
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            {member.tier}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex flex-col space-y-2">
                          {networkConnections.includes(member.id) ? (
                            <div className="flex items-center text-sm text-green-600">
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              In Your Network
                            </div>
                          ) : member.id !== 1 && (
                            <button
                              onClick={() => setNetworkConnections([...networkConnections, member.id])}
                              className="inline-block bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700 transition duration-200"
                            >
                              Add to Network
                            </button>
                          )}
                          <Link
                            to={`/directory/${member.id}`}
                            className="inline-block bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                          >
                            View Profile
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No members found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {filteredMembers.length > membersPerPage && (
            <div className="mt-12 flex justify-center">
              <nav className="inline-flex rounded-md shadow">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-l-md border border-gray-300 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>

                {/* Generate page buttons */}
                {Array.from({ length: Math.ceil(filteredMembers.length / membersPerPage) }, (_, i) => i + 1)
                  .map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 border-t border-b border-gray-300 ${currentPage === pageNum ? 'bg-apn-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {pageNum}
                    </button>
                  ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredMembers.length / membersPerPage)))}
                  disabled={currentPage === Math.ceil(filteredMembers.length / membersPerPage)}
                  className={`px-4 py-2 rounded-r-md border border-gray-300 ${currentPage === Math.ceil(filteredMembers.length / membersPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Update Your Profile</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Keep your profile information up to date to make the most of your APN membership.
          </p>
          <Link to="/profile" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Edit Your Profile
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MemberDirectory;
